// src/components/admin/attendance/LeavePlanManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, Edit, Trash2, Save, X, Users, Calendar, Settings,
    CheckCircle, XCircle, Clock, AlertCircle, FileText
} from 'lucide-react';
import leavePlanService from '../../../services/leavePlan.service';
import LeavePolicyModal from '../../common/LeavePolicyModal';
import { useEmployees } from '../../../contexts/EmployeeContext';

const LeavePlanManagement = () => {
    const [leavePlans, setLeavePlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
    const [editingLeaveType, setEditingLeaveType] = useState(null);
    const [showPolicyModal, setShowPolicyModal] = useState(false);
    const [policyPlan, setPolicyPlan] = useState(null);
    const { employees } = useEmployees();
    const leavePlanEmployeeCounts = useMemo(() => {
        return employees.reduce((counts, employee) => {
            const planId = employee.leave_plan
                ?? employee.leave_plan_id
                ?? employee.leavePlan;

            if (!planId) {
                return counts;
            }

            const key = String(planId);
            counts[key] = (counts[key] || 0) + 1;
            return counts;
        }, {});
    }, [employees]);

    useEffect(() => {
        fetchLeavePlans();
    }, []);

    const fetchLeavePlans = async () => {
        try {
            setLoading(true);
            const plans = await leavePlanService.getLeavePlans();
            setLeavePlans(plans);
        } catch (error) {
            console.error('Error fetching leave plans:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePlan = () => {
        setSelectedPlan(null);
        setShowCreateModal(true);
    };

    const handleEditPlan = async (planId) => {
        try {
            const plan = await leavePlanService.getLeavePlanById(planId);
            setSelectedPlan(plan);
            setShowCreateModal(true);
        } catch (error) {
            console.error('Error loading plan:', error);
            alert('Error loading plan details');
        }
    };

    const handleDeletePlan = async (planId) => {
        if (!window.confirm('Are you sure you want to delete this leave plan?')) {
            return;
        }

        try {
            await leavePlanService.deleteLeavePlan(planId);
            alert('Leave plan deleted successfully');
            fetchLeavePlans();
        } catch (error) {
            console.error('Error deleting plan:', error);
            alert(error.response?.data?.message || 'Error deleting plan');
        }
    };

    const handleAddLeaveType = (plan) => {
        setSelectedPlan(plan);
        setEditingLeaveType(null);
        setShowLeaveTypeModal(true);
    };

    const handleViewPolicy = (plan) => {
        setPolicyPlan(plan);
        setShowPolicyModal(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600 dark:text-gray-400">Loading leave plans...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Leave Plans</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Configure leave types and assign employees to leave plans
                    </p>
                </div>
                <button
                    onClick={handleCreatePlan}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    New Leave Plan
                </button>
            </div>

            {/* Leave Plans List */}
            {leavePlans.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 p-12 text-center">
                    <Calendar className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        No Leave Plans Yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        Create your first leave plan to start managing employee leaves
                    </p>
                    <button
                        onClick={handleCreatePlan}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        Create Leave Plan
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6">
                    {leavePlans.map((plan) => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            employeeCount={leavePlanEmployeeCounts[String(plan.id)] ?? plan.employees_count ?? 0}
                            onEdit={handleEditPlan}
                            onDelete={handleDeletePlan}
                            onAddLeaveType={handleAddLeaveType}
                            onViewPolicy={handleViewPolicy}
                            onRefresh={fetchLeavePlans}
                        />
                    ))}
                </div>
            )}

            {/* Create/Edit Plan Modal */}
            {showCreateModal && (
                <CreatePlanModal
                    plan={selectedPlan}
                    onClose={() => {
                        setShowCreateModal(false);
                        setSelectedPlan(null);
                    }}
                    onSuccess={() => {
                        setShowCreateModal(false);
                        setSelectedPlan(null);
                        fetchLeavePlans();
                    }}
                />
            )}

            {/* Add Leave Type Modal */}
            {showLeaveTypeModal && (
                <LeaveTypeModal
                    plan={selectedPlan}
                    leaveType={editingLeaveType}
                    onClose={() => {
                        setShowLeaveTypeModal(false);
                        setEditingLeaveType(null);
                    }}
                    onSuccess={() => {
                        setShowLeaveTypeModal(false);
                        setEditingLeaveType(null);
                        fetchLeavePlans();
                    }}
                />
            )}

            {/* Leave Policy Modal */}
            {showPolicyModal && policyPlan && (
                <LeavePolicyModal
                    isOpen={showPolicyModal}
                    onClose={() => {
                        setShowPolicyModal(false);
                        setPolicyPlan(null);
                    }}
                    isAdmin={true}
                    leavePlanId={policyPlan.id}
                    leaveTypes={policyPlan.leave_types || []}
                />
            )}
        </div>
    );
};

// Plan Card Component
const PlanCard = ({ plan, employeeCount, onEdit, onDelete, onAddLeaveType, onViewPolicy, onRefresh }) => {
    const [expanded, setExpanded] = useState(false);
    const [planDetails, setPlanDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const loadPlanDetails = async () => {
        if (planDetails) {
            setExpanded(!expanded);
            return;
        }

        try {
            setLoadingDetails(true);
            const details = await leavePlanService.getLeavePlanById(plan.id);
            setPlanDetails(details);
            setExpanded(true);
        } catch (error) {
            console.error('Error loading plan details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleDeleteLeaveType = async (typeId) => {
        if (!window.confirm('Are you sure you want to delete this leave type?')) {
            return;
        }

        try {
            await leavePlanService.deleteLeaveType(typeId);
            alert('Leave type deleted successfully');
            // Reload plan details
            const details = await leavePlanService.getLeavePlanById(plan.id);
            setPlanDetails(details);
            onRefresh();
        } catch (error) {
            console.error('Error deleting leave type:', error);
            alert('Error deleting leave type');
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 overflow-hidden">
            {/* Plan Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                {plan.plan_name}
                            </h3>
                            {plan.is_active ? (
                                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full">
                                    Active
                                </span>
                            ) : (
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-full">
                                    Inactive
                                </span>
                            )}
                        </div>
                        {plan.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{plan.description}</p>
                        )}
                        <div className="flex items-center gap-6 mt-3 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span>
                                    {new Date(plan.effective_from).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} -
                                    {plan.effective_to ? new Date(plan.effective_to).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Ongoing'}
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Settings className="w-4 h-4" />
                                <span>{plan.leave_types_count || 0} leave types</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                <span>{employeeCount} employees</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onViewPolicy(planDetails || plan)}
                            className="px-3 py-1.5 text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded flex items-center gap-1"
                            title="View/Edit Leave Policy"
                        >
                            <FileText className="w-4 h-4" />
                            Leave Policy
                        </button>
                        <button
                            onClick={loadPlanDetails}
                            className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                        >
                            {expanded ? 'Hide Details' : 'View Details'}
                        </button>
                        <button
                            onClick={() => onEdit(plan.id)}
                            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Edit Plan"
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => onDelete(plan.id)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title="Delete Plan"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Plan Details (Expandable) */}
            {expanded && planDetails && (
                <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
                    {/* Leave Types */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Types</h4>
                            <button
                                onClick={() => onAddLeaveType(planDetails)}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Add Leave Type
                            </button>
                        </div>
                        {planDetails.leave_types && planDetails.leave_types.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {planDetails.leave_types.map((type) => (
                                    <div
                                        key={type.id}
                                        className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                                        style={{ borderLeftWidth: '4px', borderLeftColor: type.color_code }}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <h5 className="font-semibold text-gray-900 dark:text-white">
                                                    {type.leave_type_name}
                                                </h5>
                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                    Code: {type.leave_code}
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteLeaveType(type.id)}
                                                className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="space-y-1 text-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-gray-600 dark:text-gray-400">Total Days:</span>
                                                <span className="font-medium text-gray-900 dark:text-white">{type.total_days}</span>
                                            </div>
                                            {type.carry_forward && (
                                                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                                                    <CheckCircle className="w-3 h-3" />
                                                    Carry forward: {type.max_carry_forward_days} days
                                                </div>
                                            )}
                                            {type.encashable && (
                                                <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs">
                                                    <CheckCircle className="w-3 h-3" />
                                                    Encashable
                                                </div>
                                            )}
                                            {type.min_days_notice > 0 && (
                                                <div className="flex items-center gap-1 text-orange-600 dark:text-orange-400 text-xs">
                                                    <Clock className="w-3 h-3" />
                                                    {type.min_days_notice} days notice required
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                <p>No leave types added yet</p>
                                <button
                                    onClick={() => onAddLeaveType(planDetails)}
                                    className="mt-3 text-blue-600 dark:text-blue-400 hover:underline text-sm"
                                >
                                    Add your first leave type
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Assigned Employees */}
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Assigned Employees ({planDetails.assigned_employees?.length || 0})
                        </h4>
                        {planDetails.assigned_employees && planDetails.assigned_employees.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {planDetails.assigned_employees.map((emp) => (
                                    <div
                                        key={emp.employee_id}
                                        className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                                    >
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                                                {emp.first_name} {emp.last_name}
                                            </p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">{emp.employee_code}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                                No employees assigned to this plan yet
                            </div>
                        )}
                    </div>
                </div>
            )}

            {loadingDetails && (
                <div className="p-6 text-center text-gray-600 dark:text-gray-400">
                    Loading plan details...
                </div>
            )}
        </div>
    );
};

// Create/Edit Plan Modal
const CreatePlanModal = ({ plan, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        plan_name: plan?.plan_name || '',
        description: plan?.description || '',
        effective_from: plan?.effective_from?.split('T')[0] || '',
        effective_to: plan?.effective_to?.split('T')[0] || '',
        leave_types: plan?.leave_types || []
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.plan_name || !formData.effective_from) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSaving(true);
            if (plan) {
                await leavePlanService.updateLeavePlan(plan.id, formData);
                alert('Leave plan updated successfully');
            } else {
                await leavePlanService.createLeavePlan(formData);
                alert('Leave plan created successfully');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving plan:', error);
            alert(error.response?.data?.message || 'Error saving plan');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                onClick={onClose}
            />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {plan ? 'Edit Leave Plan' : 'Create New Leave Plan'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Plan Name *
                        </label>
                        <input
                            type="text"
                            value={formData.plan_name}
                            onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Effective From *
                            </label>
                            <input
                                type="date"
                                value={formData.effective_from}
                                onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Effective To
                            </label>
                            <input
                                type="date"
                                value={formData.effective_to}
                                onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : plan ? 'Update Plan' : 'Create Plan'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

// Leave Type Modal Component
const LeaveTypeModal = ({ plan, leaveType, onClose, onSuccess }) => {
    const predefinedTypes = leavePlanService.getPredefinedLeaveTypes();
    const [showPredefined, setShowPredefined] = useState(!leaveType);
    const [formData, setFormData] = useState(leaveType || {
        leave_type_name: '',
        leave_code: '',
        total_days: 0,
        carry_forward: false,
        max_carry_forward_days: 0,
        encashable: false,
        requires_approval: true,
        min_days_notice: 0,
        max_consecutive_days: null,
        prorate_on_joining: true,
        color_code: '#3B82F6',
        is_custom: true
    });
    const [saving, setSaving] = useState(false);

    const handleSelectPredefined = (type) => {
        setFormData({ ...type, is_custom: false });
        setShowPredefined(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.leave_type_name || !formData.leave_code) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSaving(true);
            if (leaveType) {
                await leavePlanService.updateLeaveType(leaveType.id, formData);
                alert('Leave type updated successfully');
            } else {
                await leavePlanService.addLeaveType(plan.id, formData);
                alert('Leave type added successfully');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving leave type:', error);
            alert(error.response?.data?.message || 'Error saving leave type');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                onClick={onClose}
            />
            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {leaveType ? 'Edit Leave Type' : 'Add Leave Type'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {showPredefined && !leaveType && (
                    <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                            Choose from Predefined Leave Types
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            {predefinedTypes.map((type) => (
                                <button
                                    key={type.leave_code}
                                    type="button"
                                    onClick={() => handleSelectPredefined(type)}
                                    className="p-3 text-left border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                                    style={{ borderLeftWidth: '4px', borderLeftColor: type.color_code }}
                                >
                                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                                        {type.leave_type_name}
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        {type.leave_code} - {type.total_days} days
                                    </div>
                                </button>
                            ))}
                        </div>
                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowPredefined(false)}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Or create a custom leave type
                            </button>
                        </div>
                    </div>
                )}

                {(!showPredefined || leaveType) && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Leave Type Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.leave_type_name}
                                    onChange={(e) => setFormData({ ...formData, leave_type_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Leave Code *
                                </label>
                                <input
                                    type="text"
                                    value={formData.leave_code}
                                    onChange={(e) => setFormData({ ...formData, leave_code: e.target.value.toUpperCase() })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    maxLength={10}
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Total Days *
                                </label>
                                <input
                                    type="number"
                                    value={formData.total_days}
                                    onChange={(e) => setFormData({ ...formData, total_days: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    min="0"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Color
                                </label>
                                <input
                                    type="color"
                                    value={formData.color_code}
                                    onChange={(e) => setFormData({ ...formData, color_code: e.target.value })}
                                    className="w-full h-10 px-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.carry_forward}
                                        onChange={(e) => setFormData({ ...formData, carry_forward: e.target.checked })}
                                        className="w-4 h-4 text-blue-600 rounded"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Allow Carry Forward</span>
                                </label>
                            </div>
                            {formData.carry_forward && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Max Carry Forward Days
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.max_carry_forward_days}
                                        onChange={(e) => setFormData({ ...formData, max_carry_forward_days: parseInt(e.target.value) || 0 })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min="0"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.encashable}
                                    onChange={(e) => setFormData({ ...formData, encashable: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Encashable</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.requires_approval}
                                    onChange={(e) => setFormData({ ...formData, requires_approval: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Requires Approval</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.prorate_on_joining}
                                    onChange={(e) => setFormData({ ...formData, prorate_on_joining: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">Prorate on Joining</span>
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Minimum Days Notice
                                </label>
                                <input
                                    type="number"
                                    value={formData.min_days_notice}
                                    onChange={(e) => setFormData({ ...formData, min_days_notice: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    min="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Max Consecutive Days
                                </label>
                                <input
                                    type="number"
                                    value={formData.max_consecutive_days || ''}
                                    onChange={(e) => setFormData({ ...formData, max_consecutive_days: e.target.value ? parseInt(e.target.value) : null })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    min="1"
                                    placeholder="No limit"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            {!leaveType && (
                                <button
                                    type="button"
                                    onClick={() => setShowPredefined(true)}
                                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                                >
                                    Choose Predefined
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                            >
                                <Save className="w-4 h-4" />
                                {saving ? 'Saving...' : leaveType ? 'Update' : 'Add Leave Type'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </>
    );
};

export default LeavePlanManagement;
